// StaffCalendar — staff-facing month-grid calendar over the bookings collection.
//
// Unlike the public /events calendar (config/publicEvents, polished copy only),
// this shows EVERY booking with a date — holds, inquiries, non-public gigs —
// color-coded by status. Click a day to see its events or quick-add a new one.
// Quick-add writes a real bookings doc, so resyncPublicOnBookingWrite fans the
// event out to the public site automatically when "Show on public website" is
// checked. The ⟳ sync button runs the full one-way Google Calendar sync
// (adopt/update/stage/archive — see syncPublicCalendarCore in functions).
//
// 📥 Inbox: the ONE place ambiguity surfaces — unlinked Square invoices and
// GCal events that landed on a day that already has a booking. Items are
// resolved through the resolveBookingInbox callable (attach / create / adopt /
// dismiss); the badge count is live via onSnapshot.

import { useEffect, useMemo, useState } from "react";
import {
  addDoc, collection, doc, getDoc, onSnapshot, query, serverTimestamp,
  setDoc, Timestamp, where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { db } from "../../firebase";
import { useBookings } from "../crm/hooks/useBookings";
import { defaultBooking, STATUS, STATUS_LABELS } from "../crm/schema";
import { STAFF_THEME, FONT } from "../../theme/staff";
import AddressAutocomplete from "../../components/AddressAutocomplete";

const BRAND = { ...STAFF_THEME, bg: STAFF_THEME.dark, line: STAFF_THEME.raised };

// Status → calendar color. Meaning-bearing, not aesthetic: green = locked in,
// gold = money down, sky = still in the pipeline, orange = soft hold.
// "confirmed" isn't in the STATUS enum but exists on older PublicEventQuickAdd
// docs — treat it as booked.
const STATUS_COLORS = {
  [STATUS.BOOKED]: BRAND.green,
  confirmed: BRAND.green,
  [STATUS.COMPLETED]: "rgba(255,255,255,0.45)",
  [STATUS.DEPOSIT_RECEIVED]: BRAND.gold,
  [STATUS.HOLD]: BRAND.orange,
  [STATUS.NEW_INQUIRY]: BRAND.sky,
  [STATUS.CONTACTED]: BRAND.sky,
  [STATUS.QUOTED]: BRAND.sky,
};

function statusColor(s) {
  return STATUS_COLORS[s] || BRAND.sky;
}

function statusLabel(s) {
  if (s === "confirmed") return "Confirmed";
  return STATUS_LABELS[s] || (s ? s.replace(/_/g, " ") : "—");
}

function toDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts instanceof Date) return ts;
  if (typeof ts === "string" || typeof ts === "number") return new Date(ts);
  return null;
}

// Local-timezone day key. confirmedDate is stored as Chicago midnight, and
// staff devices live in Chicago, so local date parts are the right read.
function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function bookingDay(b) {
  return toDate(b.confirmedDate) || toDate(b.confirmedStart);
}

function bookingTitle(b) {
  return b.publicTitle || b.eventName || b.venueName || "(untitled)";
}

function fmtTime(ts) {
  const d = toDate(ts);
  if (!d) return null;
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "");
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_FMT = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });

export default function StaffCalendar({ navigate }) {
  const { bookings, loading } = useBookings();
  const now = new Date();
  const [monthDate, setMonthDate] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [selectedKey, setSelectedKey] = useState(null); // "YYYY-MM-DD" → day panel open
  const [addForKey, setAddForKey] = useState(null);     // "YYYY-MM-DD" → quick-add open
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inboxItems, setInboxItems] = useState([]);

  // Live open-question count — the badge is only honest if it's a listener.
  useEffect(() => {
    const q = query(collection(db, "bookingInbox"), where("status", "==", "open"));
    return onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setInboxItems(items);
    }, (err) => console.warn("bookingInbox listener failed", err?.message || err));
  }, []);

  const todayKey = dateKey(now);

  // Index calendar-worthy bookings by day. Spam/declined/archived/test docs
  // aren't happening — keep them off the calendar (they stay in the CRM list).
  const byDate = useMemo(() => {
    const map = new Map();
    for (const b of bookings) {
      if (b._isTestData || b.archivedAt) continue;
      if (b.status === STATUS.SPAM || b.status === STATUS.DECLINED || b.status === STATUS.NO_RESPONSE) continue;
      const day = bookingDay(b);
      if (!day) continue;
      const key = dateKey(day);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(b);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (toDate(a.confirmedStart) || 0) - (toDate(b.confirmedStart) || 0));
    }
    return map;
  }, [bookings]);

  // Month grid: leading nulls pad to the first weekday, then one Date per day.
  const cells = useMemo(() => {
    const y = monthDate.getFullYear();
    const m = monthDate.getMonth();
    const out = [];
    for (let i = 0; i < new Date(y, m, 1).getDay(); i++) out.push(null);
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) out.push(new Date(y, m, d));
    return out;
  }, [monthDate]);

  function shiftMonth(delta) {
    setMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  }

  async function doImport() {
    if (importBusy) return;
    setImportBusy(true);
    setImportResult(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "importCalendarEvents");
      const res = await fn({});
      const { created = 0, updated = 0, adopted = 0, staged = 0, archived = 0 } = res.data || {};
      const parts = [];
      if (created)  parts.push(`${created} new`);
      if (updated)  parts.push(`${updated} updated`);
      if (adopted)  parts.push(`${adopted} matched to existing`);
      if (archived) parts.push(`${archived} removed`);
      if (staged)   parts.push(`${staged} question${staged === 1 ? "" : "s"} → 📥`);
      setImportResult({ ok: true, text: parts.length ? `✓ Synced: ${parts.join(", ")}` : "✓ Up to date — calendar and bookings already match" });
    } catch (err) {
      setImportResult({ ok: false, text: err?.message || "Sync failed" });
    } finally {
      setImportBusy(false);
    }
  }

  const selectedDate = selectedKey ? new Date(`${selectedKey}T00:00:00`) : null;
  const selectedEvents = selectedKey ? byDate.get(selectedKey) || [] : [];

  return (
    <div style={{ minHeight: "100vh", background: BRAND.bg, color: BRAND.cream, fontFamily: FONT.body }}>
      {/* Header */}
      <div style={{ padding: "0.75rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.line}`, display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
        <div style={{ color: BRAND.gold, fontFamily: FONT.display, fontWeight: 700, fontSize: "1.05rem", flex: 1, minWidth: "120px" }}>📅 Calendar</div>
        <button
          onClick={() => setInboxOpen(true)}
          style={{
            ...ghostBtn,
            borderColor: inboxItems.length ? "rgba(252,179,31,0.65)" : ghostBtn.border,
            color: inboxItems.length ? BRAND.gold : ghostBtn.color,
            fontWeight: 700,
          }}
        >
          📥 Inbox{inboxItems.length ? ` (${inboxItems.length})` : ""}
        </button>
        <button onClick={doImport} disabled={importBusy} style={{ ...ghostBtn, opacity: importBusy ? 0.6 : 1 }}>
          {importBusy ? "Syncing…" : "⟳ Sync Google Calendar"}
        </button>
        <button onClick={() => navigate("/crm")} style={ghostBtn}>📇 Bookings</button>
        <button onClick={() => setSettingsOpen(true)} style={ghostBtn} aria-label="Calendar sync settings">⚙️</button>
        <button onClick={() => navigate("/menu")} style={ghostBtn}>← Menu</button>
      </div>

      {importResult && (
        <div style={{ margin: "0.6rem 1rem 0", padding: "0.5rem 0.8rem", borderRadius: "8px", fontSize: "0.82rem", fontWeight: 600, background: importResult.ok ? "rgba(112,190,68,0.14)" : "rgba(231,76,60,0.14)", border: `1px solid ${importResult.ok ? "rgba(112,190,68,0.45)" : "rgba(231,76,60,0.45)"}`, color: importResult.ok ? BRAND.green : "#ff8a8a" }}>
          {importResult.text}
        </div>
      )}

      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "0.9rem 0.8rem 3rem" }}>
        {/* Month nav */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.7rem" }}>
          <button onClick={() => shiftMonth(-1)} style={navBtn} aria-label="Previous month">‹</button>
          <div style={{ flex: 1, textAlign: "center", color: "white", fontFamily: FONT.display, fontWeight: 700, fontSize: "1.15rem" }}>
            {MONTH_FMT.format(monthDate)}
          </div>
          <button onClick={() => shiftMonth(1)} style={navBtn} aria-label="Next month">›</button>
          <button onClick={() => setMonthDate(new Date(now.getFullYear(), now.getMonth(), 1))} style={{ ...ghostBtn, padding: "0.4rem 0.7rem" }}>Today</button>
        </div>

        {/* Weekday header */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", marginBottom: "4px" }}>
          {WEEKDAYS.map((w) => (
            <div key={w} style={{ textAlign: "center", color: BRAND.dim, fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "0.2rem 0" }}>{w}</div>
          ))}
        </div>

        {/* Month grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px" }}>
          {cells.map((d, i) => {
            if (!d) return <div key={`pad-${i}`} />;
            const key = dateKey(d);
            const events = byDate.get(key) || [];
            const isToday = key === todayKey;
            return (
              <button
                key={key}
                onClick={() => setSelectedKey(key)}
                style={{
                  background: selectedKey === key ? BRAND.raised : BRAND.card,
                  border: isToday ? `2px solid ${BRAND.gold}` : `1px solid ${BRAND.divider}`,
                  borderRadius: "10px",
                  padding: "0.3rem 0.25rem 0.35rem",
                  minHeight: "clamp(3.4rem, 11vw, 5.6rem)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: "2px",
                  cursor: "pointer",
                  textAlign: "left",
                  overflow: "hidden",
                  fontFamily: "inherit",
                }}
              >
                <span style={{ color: isToday ? BRAND.gold : BRAND.muted, fontSize: "0.72rem", fontWeight: isToday ? 800 : 600, lineHeight: 1 }}>{d.getDate()}</span>
                {events.slice(0, 2).map((b) => (
                  <span key={b.id} style={{ display: "flex", alignItems: "center", gap: "3px", minWidth: 0 }}>
                    <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: statusColor(b.status), flexShrink: 0 }} />
                    <span style={{ color: "white", fontSize: "0.62rem", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{bookingTitle(b)}</span>
                  </span>
                ))}
                {events.length > 2 && (
                  <span style={{ color: BRAND.dim, fontSize: "0.6rem", fontWeight: 700 }}>+{events.length - 2} more</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: "0.9rem", flexWrap: "wrap", marginTop: "0.8rem", paddingLeft: "0.2rem" }}>
          {[["Booked", BRAND.green], ["Deposit in", BRAND.gold], ["Inquiry / quoted", BRAND.sky], ["Hold", BRAND.orange], ["Done", "rgba(255,255,255,0.45)"]].map(([label, color]) => (
            <span key={label} style={{ display: "flex", alignItems: "center", gap: "5px", color: BRAND.muted, fontSize: "0.7rem", fontWeight: 600 }}>
              <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: color }} />{label}
            </span>
          ))}
        </div>

        {loading && <div style={{ color: BRAND.dim, fontSize: "0.85rem", marginTop: "1rem", textAlign: "center" }}>Loading bookings…</div>}
      </div>

      {/* Day panel */}
      {selectedKey && !addForKey && (
        <Overlay onClose={() => setSelectedKey(null)}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.8rem" }}>
            <div style={{ color: BRAND.gold, fontFamily: FONT.display, fontWeight: 700, fontSize: "1.05rem", flex: 1 }}>
              {selectedDate.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
            </div>
            <button onClick={() => setSelectedKey(null)} style={ghostBtn}>✕</button>
          </div>

          {selectedEvents.length === 0 && (
            <div style={{ color: BRAND.dim, fontSize: "0.88rem", padding: "0.8rem 0 1rem" }}>Nothing on the griddle this day.</div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
            {selectedEvents.map((b) => {
              const start = fmtTime(b.confirmedStart);
              const end = fmtTime(b.confirmedEnd);
              return (
                <button key={b.id} onClick={() => navigate(`/crm/bookings/${b.id}`)} style={{ background: BRAND.card, border: `1px solid ${BRAND.divider}`, borderRadius: "10px", padding: "0.6rem 0.75rem", cursor: "pointer", textAlign: "left", display: "flex", flexDirection: "column", gap: "3px", fontFamily: "inherit" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span style={{ color: "white", fontWeight: 700, fontSize: "0.92rem", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {b.isPublicEvent ? "🌐 " : ""}{bookingTitle(b)}
                    </span>
                    <span style={{ background: `${statusColor(b.status)}22`, color: statusColor(b.status), border: `1px solid ${statusColor(b.status)}66`, borderRadius: "999px", padding: "0.1rem 0.5rem", fontSize: "0.66rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                      {statusLabel(b.status)}
                    </span>
                  </div>
                  <div style={{ color: BRAND.muted, fontSize: "0.76rem" }}>
                    {[start && (end ? `${start}–${end}` : start), b.venueName || b.venueAddress].filter(Boolean).join(" · ") || "No time / venue yet"}
                  </div>
                </button>
              );
            })}
          </div>

          <button onClick={() => setAddForKey(selectedKey)} style={primaryBtn}>+ New event</button>
        </Overlay>
      )}

      {/* Quick-add */}
      {addForKey && (
        <QuickAddModal
          initialDate={addForKey}
          navigate={navigate}
          onClose={() => setAddForKey(null)}
          onSaved={(key) => { setAddForKey(null); setSelectedKey(key); }}
        />
      )}

      {/* Triage inbox */}
      {inboxOpen && (
        <InboxPanel items={inboxItems} bookings={bookings} navigate={navigate} onClose={() => setInboxOpen(false)} />
      )}

      {/* Sync settings */}
      {settingsOpen && <SyncSettingsModal onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

// ── Triage inbox ────────────────────────────────────────────────────────────
// The single surface where the capture pipes are allowed to ask questions.
// Every card resolves through the resolveBookingInbox callable; the list
// updates itself via the parent's onSnapshot.

function fmtCents(cents) {
  if (cents == null) return null;
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

function InboxPanel({ items, bookings, navigate, onClose }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [sweepBusy, setSweepBusy] = useState(false);
  const [sweepNote, setSweepNote] = useState(null);
  // Per-item expanded action: { itemId, mode: "pick" | "create" }
  const [expanded, setExpanded] = useState(null);

  async function resolve(itemId, action, extra = {}) {
    if (busyId) return;
    setBusyId(itemId);
    setError(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "resolveBookingInbox");
      await fn({ itemId, action, ...extra });
      setExpanded(null);
    } catch (e) {
      setError(e?.message || "Action failed");
    } finally {
      setBusyId(null);
    }
  }

  async function doSweep() {
    if (sweepBusy) return;
    setSweepBusy(true);
    setSweepNote(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "sweepSquareInvoiceInboxNow");
      const res = await fn({});
      const { staged = 0 } = res.data || {};
      setSweepNote(staged ? `Found ${staged} new invoice${staged === 1 ? "" : "s"}` : "Nothing new in Square");
    } catch (e) {
      setSweepNote(e?.message || "Sweep failed");
    } finally {
      setSweepBusy(false);
    }
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.8rem" }}>
        <div style={{ color: BRAND.gold, fontFamily: FONT.display, fontWeight: 700, fontSize: "1.05rem", flex: 1 }}>
          📥 Inbox{items.length ? ` — ${items.length} to sort` : ""}
        </div>
        <button onClick={doSweep} disabled={sweepBusy} style={{ ...ghostBtn, opacity: sweepBusy ? 0.6 : 1 }}>
          {sweepBusy ? "Checking…" : "⟳ Check Square"}
        </button>
        <button onClick={onClose} style={ghostBtn}>✕</button>
      </div>

      {sweepNote && <div style={{ color: BRAND.muted, fontSize: "0.78rem", marginBottom: "0.6rem" }}>{sweepNote}</div>}
      {error && (
        <div style={{ color: "#ff8a8a", fontSize: "0.82rem", padding: "0.5rem 0.75rem", background: "rgba(201,68,68,0.12)", border: "1px solid rgba(201,68,68,0.4)", borderRadius: "8px", marginBottom: "0.6rem" }}>
          {error}
        </div>
      )}

      {items.length === 0 && (
        <div style={{ color: BRAND.dim, fontSize: "0.9rem", padding: "1.2rem 0 1.4rem", textAlign: "center" }}>
          ✨ All clear — nothing needs sorting.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        {items.map((item) => (
          <InboxCard
            key={item.id}
            item={item}
            bookings={bookings}
            navigate={navigate}
            busy={busyId === item.id}
            expanded={expanded?.itemId === item.id ? expanded.mode : null}
            setExpanded={(mode) => setExpanded(mode ? { itemId: item.id, mode } : null)}
            resolve={resolve}
          />
        ))}
      </div>
    </Overlay>
  );
}

function InboxCard({ item, bookings, navigate, busy, expanded, setExpanded, resolve }) {
  const cardStyle = {
    background: BRAND.card, border: `1px solid ${BRAND.divider}`, borderRadius: "10px",
    padding: "0.65rem 0.75rem", display: "flex", flexDirection: "column", gap: "0.45rem",
    opacity: busy ? 0.55 : 1,
  };
  const actionRow = { display: "flex", gap: "0.4rem", flexWrap: "wrap" };
  const smallBtn = { ...ghostBtn, padding: "0.32rem 0.6rem", fontSize: "0.74rem" };
  const goldBtn = { ...smallBtn, borderColor: "rgba(252,179,31,0.6)", color: BRAND.gold };

  if (item.type === "square_invoice") {
    const amount = fmtCents(item.amountCents);
    return (
      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
          <span style={{ color: "white", fontWeight: 700, fontSize: "0.9rem", flex: 1, minWidth: 0 }}>
            🧾 {item.invoiceTitle || `Invoice ${item.invoiceNumber || item.invoiceId?.slice(0, 8)}`}
          </span>
          <span style={{ color: BRAND.gold, fontSize: "0.8rem", fontWeight: 700, whiteSpace: "nowrap" }}>{amount || ""}</span>
        </div>
        <div style={{ color: BRAND.muted, fontSize: "0.75rem" }}>
          {[item.customerName, item.invoiceStatus, item.invoiceCreatedAt && new Date(item.invoiceCreatedAt).toLocaleDateString()].filter(Boolean).join(" · ")}
          <span style={{ color: BRAND.dim }}> — Square invoice with no booking yet</span>
        </div>
        {!expanded && (
          <div style={actionRow}>
            <button onClick={() => setExpanded("pick")} disabled={busy} style={goldBtn}>🔗 Link to booking</button>
            <button onClick={() => setExpanded("create")} disabled={busy} style={smallBtn}>＋ New booking</button>
            <button onClick={() => resolve(item.id, "dismiss")} disabled={busy} style={smallBtn}>Dismiss</button>
          </div>
        )}
        {expanded === "pick" && (
          <BookingPicker
            bookings={bookings}
            hint="Pick the booking this invoice belongs to:"
            onPick={(b) => resolve(item.id, "attachInvoice", { bookingId: b.id })}
            onCancel={() => setExpanded(null)}
          />
        )}
        {expanded === "create" && (
          <MiniCreateForm
            initialName={item.invoiceTitle || (item.customerName ? `${item.customerName} event` : "")}
            busy={busy}
            onSubmit={(fields) => resolve(item.id, "createBookingFromInvoice", { fields })}
            onCancel={() => setExpanded(null)}
          />
        )}
      </div>
    );
  }

  if (item.type === "gcal_event") {
    return (
      <div style={cardStyle}>
        <div style={{ color: "white", fontWeight: 700, fontSize: "0.9rem" }}>📅 {item.summary}</div>
        <div style={{ color: BRAND.muted, fontSize: "0.75rem" }}>
          {[item.dayKey, item.venueName].filter(Boolean).join(" · ")}
          <span style={{ color: BRAND.dim }}> — new on Google Calendar, but that day already has a booking. Same event?</span>
        </div>
        <div style={actionRow}>
          {(item.candidates || []).map((c) => (
            <button key={c.id} onClick={() => resolve(item.id, "adoptGcal", { bookingId: c.id })} disabled={busy} style={goldBtn}>
              ＝ Same as “{c.title}”
            </button>
          ))}
          <button onClick={() => resolve(item.id, "createBookingFromGcal")} disabled={busy} style={smallBtn}>＋ Separate event</button>
          <button onClick={() => resolve(item.id, "dismiss")} disabled={busy} style={smallBtn}>Dismiss</button>
        </div>
      </div>
    );
  }

  if (item.type === "gcal_removed") {
    return (
      <div style={cardStyle}>
        <div style={{ color: "white", fontWeight: 700, fontSize: "0.9rem" }}>⚠️ {item.title}</div>
        <div style={{ color: BRAND.muted, fontSize: "0.75rem" }}>
          {item.dayKey}
          <span style={{ color: BRAND.dim }}> — removed from Google Calendar, but this booking has real links (shift / invoice / client), so nothing was touched.</span>
        </div>
        <div style={actionRow}>
          <button onClick={() => resolve(item.id, "keepBooking")} disabled={busy} style={goldBtn}>Keep booking (just un-public)</button>
          <button onClick={() => resolve(item.id, "archiveBooking")} disabled={busy} style={smallBtn}>🗄 Archive it</button>
          <button onClick={() => navigate(`/crm/bookings/${item.bookingId}`)} style={smallBtn}>View →</button>
        </div>
      </div>
    );
  }

  return null;
}

// Compact booking chooser: upcoming-first, invoice-free bookings, live filter.
function BookingPicker({ bookings, hint, onPick, onCancel }) {
  const [filter, setFilter] = useState("");
  const candidates = useMemo(() => {
    const now = Date.now();
    const list = bookings.filter((b) =>
      !b.archivedAt && !b._isTestData &&
      !b.squareInvoiceId && !b.squareDepositInvoiceId && !b.squareBalanceInvoiceId);
    const t = filter.trim().toLowerCase();
    const filtered = t
      ? list.filter((b) => [b.eventName, b.publicTitle, b.venueName, b.name, b.company].filter(Boolean).join(" ").toLowerCase().includes(t))
      : list;
    const dayMs = (b) => toDate(b.confirmedDate)?.getTime() ?? toDate(b.confirmedStart)?.getTime() ?? null;
    return filtered.sort((a, b) => {
      const da = dayMs(a), db_ = dayMs(b);
      const fa = da != null && da >= now, fb = db_ != null && db_ >= now;
      if (fa !== fb) return fa ? -1 : 1;           // upcoming first
      if (fa && fb) return da - db_;               // soonest upcoming
      if (da == null || db_ == null) return da == null ? (db_ == null ? 0 : -1) : 1; // dateless before past
      return db_ - da;                             // recent past
    }).slice(0, 12);
  }, [bookings, filter]);

  return (
    <div style={{ background: BRAND.panel, border: `1px solid ${BRAND.line}`, borderRadius: "8px", padding: "0.55rem", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
      <div style={{ color: BRAND.cream, fontSize: "0.74rem", fontWeight: 600 }}>{hint}</div>
      <input
        type="text" value={filter} onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter by event, venue, or client…" style={{ ...inputStyle, fontSize: "0.8rem", padding: "0.4rem 0.55rem" }} autoFocus
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", maxHeight: "220px", overflowY: "auto" }}>
        {candidates.length === 0 && <div style={{ color: BRAND.dim, fontSize: "0.75rem", padding: "0.3rem 0" }}>No invoice-free bookings match.</div>}
        {candidates.map((b) => {
          const day = toDate(b.confirmedDate) || toDate(b.confirmedStart);
          return (
            <button key={b.id} onClick={() => onPick(b)} style={{ ...ghostBtn, textAlign: "left", display: "flex", gap: "0.5rem", alignItems: "baseline", fontSize: "0.78rem" }}>
              <span style={{ color: "white", fontWeight: 600, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{bookingTitle(b)}</span>
              <span style={{ color: BRAND.muted, whiteSpace: "nowrap" }}>{day ? day.toLocaleDateString([], { month: "short", day: "numeric" }) : "no date"}</span>
            </button>
          );
        })}
      </div>
      <button onClick={onCancel} style={{ ...ghostBtn, alignSelf: "flex-start", fontSize: "0.72rem", padding: "0.3rem 0.55rem" }}>Cancel</button>
    </div>
  );
}

// Two fields, one button: enough to mint a curated booking from an invoice.
// Date is optional — a dateless booking stays off the calendar grid until
// it's filled in, and pollSquareInvoices owns status from here.
function MiniCreateForm({ initialName, busy, onSubmit, onCancel }) {
  const [eventName, setEventName] = useState(initialName || "");
  const [date, setDate] = useState("");
  return (
    <div style={{ background: BRAND.panel, border: `1px solid ${BRAND.line}`, borderRadius: "8px", padding: "0.55rem", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
      <input type="text" value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="Event name" style={{ ...inputStyle, fontSize: "0.8rem", padding: "0.4rem 0.55rem" }} autoFocus />
      <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...inputStyle, fontSize: "0.8rem", padding: "0.4rem 0.55rem", flex: 1 }} />
        <span style={{ color: BRAND.dim, fontSize: "0.68rem" }}>date optional</span>
      </div>
      <div style={{ display: "flex", gap: "0.4rem" }}>
        <button
          onClick={() => onSubmit({ eventName: eventName.trim(), date })}
          disabled={busy || !eventName.trim()}
          style={{ ...primaryBtn, width: "auto", padding: "0.4rem 0.9rem", fontSize: "0.78rem", opacity: eventName.trim() ? 1 : 0.5 }}
        >
          ✓ Create + link invoice
        </button>
        <button onClick={onCancel} style={{ ...ghostBtn, fontSize: "0.72rem" }}>Cancel</button>
      </div>
    </div>
  );
}

// ── Sync settings ───────────────────────────────────────────────────────────
// One field: the internal calendar the booking mirror projects onto. Blank =
// mirror off. Must NOT be the public events calendar (that one is a SOURCE;
// pointing the mirror at it would loop imports).
function SyncSettingsModal({ onClose }) {
  const [calId, setCalId] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);

  useEffect(() => {
    getDoc(doc(db, "config", "calendarSync"))
      .then((snap) => { setCalId(snap.exists() ? snap.data().internalCalendarId || "" : ""); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  async function save() {
    if (saving) return;
    setSaving(true);
    setNote(null);
    try {
      await setDoc(doc(db, "config", "calendarSync"), {
        internalCalendarId: calId.trim() || null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setNote({ ok: true, text: calId.trim() ? "✓ Saved — invoiced bookings will mirror onto this calendar." : "✓ Saved — mirror is off." });
    } catch (e) {
      setNote({ ok: false, text: e?.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.8rem" }}>
        <div style={{ color: BRAND.gold, fontFamily: FONT.display, fontWeight: 700, fontSize: "1.05rem", flex: 1 }}>⚙️ Calendar sync</div>
        <button onClick={onClose} style={ghostBtn}>✕</button>
      </div>

      <div style={{ color: BRAND.muted, fontSize: "0.8rem", lineHeight: 1.5, marginBottom: "0.7rem" }}>
        <strong style={{ color: BRAND.cream }}>Public calendar → website</strong> runs automatically every 2 hours (or ⟳ anytime).
        <br /><br />
        <strong style={{ color: BRAND.cream }}>Internal mirror</strong>: bookings with a Square invoice appear on the calendar below —
        ⏳ HOLD when the invoice goes out, ✓ when the deposit lands. Paste the calendar’s ID from Google Calendar
        → Settings → “Integrate calendar”, and share that calendar with
        <code style={{ color: BRAND.gold, fontSize: "0.72rem" }}> bookings-calendar-sync@the-golden-ticket-system.iam.gserviceaccount.com </code>
        as “Make changes to events”. Use a separate internal calendar — never the public events one.
      </div>

      <Field label="Internal calendar ID" hint="Blank = mirror off.">
        <input type="text" value={calId} onChange={(e) => setCalId(e.target.value)} placeholder="abc123…@group.calendar.google.com" style={inputStyle} disabled={!loaded} />
      </Field>

      {note && (
        <div style={{ color: note.ok ? BRAND.green : "#ff8a8a", fontSize: "0.8rem", margin: "0.5rem 0" }}>{note.text}</div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.5rem" }}>
        <button onClick={save} disabled={saving || !loaded} style={{ ...primaryBtn, width: "auto", padding: "0.5rem 1.1rem" }}>
          {saving ? "Saving…" : "✓ Save"}
        </button>
      </div>
    </Overlay>
  );
}

// ── Quick-add modal ─────────────────────────────────────────────────────────
// Minimal booking creation: the fields both calendars need, nothing else.
// Complex cases (clients, contracts, series) go through the full CRM forms.
function QuickAddModal({ initialDate, navigate, onClose, onSaved }) {
  const [eventName, setEventName] = useState("");
  const [date, setDate] = useState(initialDate);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [venueName, setVenueName] = useState("");
  const [venueAddress, setVenueAddress] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [publicTitle, setPublicTitle] = useState("");
  const [publicDescription, setPublicDescription] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const canSave = eventName.trim() && date;

  function combineDateTime(dateStr, timeStr) {
    if (!dateStr || !timeStr) return null;
    const d = new Date(`${dateStr}T${timeStr}:00`);
    return isNaN(d) ? null : Timestamp.fromDate(d);
  }

  async function save() {
    if (!canSave || saving) return;
    setSaving(true);
    setError(null);
    try {
      const docData = {
        ...defaultBooking(),
        source: "staff_calendar",
        eventName: eventName.trim(),
        // confirmedDate = local (Chicago) midnight — the day anchor every
        // consumer partitions on. Start/end carry the wall-clock times.
        confirmedDate: Timestamp.fromDate(new Date(`${date}T00:00:00`)),
        confirmedStart: combineDateTime(date, startTime),
        confirmedEnd: combineDateTime(date, endTime),
        venueName: venueName.trim() || null,
        venueAddress: venueAddress.trim() || null,
        publicTitle: (isPublic && publicTitle.trim()) || null,
        publicDescription: (isPublic && publicDescription.trim()) || null,
        internalNotes: internalNotes.trim() || null,
        isPublicEvent: isPublic,
        status: STATUS.BOOKED,
        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await addDoc(collection(db, "bookings"), docData);
      onSaved(date);
    } catch (e) {
      console.error("StaffCalendar quick-add failed", e);
      setError(e.message || "Save failed.");
      setSaving(false);
    }
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.9rem" }}>
        <div style={{ color: BRAND.gold, fontFamily: FONT.display, fontWeight: 700, fontSize: "1.05rem", flex: 1 }}>＋ New event</div>
        <button
          onClick={() => navigate(`/produce?${new URLSearchParams({
            ...(eventName.trim() ? { title: eventName.trim() } : {}),
            ...(date ? { date } : {}),
            ...(startTime ? { start: startTime } : {}),
            ...(venueAddress.trim() ? { address: venueAddress.trim() } : {}),
          }).toString()}`)}
          title="Hand this to the Event Producer — dictate a brief, it researches links + drafts the copy"
          style={{ ...ghostBtn, fontSize: "0.74rem" }}
        >🎬 Producer →</button>
        <button onClick={onClose} style={ghostBtn}>✕</button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
        <Field label="Event name *">
          <input type="text" value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. Tower Grove Sat market" style={inputStyle} autoFocus />
        </Field>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Field label="Date *" grow={1.4}>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Start">
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="End">
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} style={inputStyle} />
          </Field>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Field label="Venue name">
            <input type="text" value={venueName} onChange={(e) => setVenueName(e.target.value)} placeholder="Tower Grove Farmers Market" style={inputStyle} />
          </Field>
          <Field label="Venue address" grow={1.4}>
            <AddressAutocomplete value={venueAddress} onChange={setVenueAddress} placeholder="4256 Magnolia Ave, St. Louis MO" inputStyle={inputStyle} />
          </Field>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: "0.55rem", padding: "0.55rem 0.7rem", background: BRAND.panel, border: `1px solid ${isPublic ? "rgba(112,190,68,0.5)" : BRAND.line}`, borderRadius: "8px", cursor: "pointer", fontSize: "0.88rem" }}>
          <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} style={{ accentColor: BRAND.green }} />
          <span style={{ color: BRAND.cream, fontWeight: 600 }}>
            🌐 Show on public website
            <span style={{ display: "block", color: BRAND.dim, fontSize: "0.72rem", fontWeight: 400 }}>
              {isPublic ? "Goes live on thejoyofpancakes.com/events within seconds of saving." : "Internal only — holds, prep days, private gigs."}
            </span>
          </span>
        </label>

        {isPublic && (
          <>
            <Field label="Public title" hint="Optional — uses the event name if blank.">
              <input type="text" value={publicTitle} onChange={(e) => setPublicTitle(e.target.value)} placeholder="Pancake Art at Tower Grove Farmers Market" style={inputStyle} />
            </Field>
            <Field label="Public description" hint="1–3 sentences for the public listing.">
              <textarea value={publicDescription} onChange={(e) => setPublicDescription(e.target.value)} rows={2} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
            </Field>
          </>
        )}

        <Field label="Internal notes" hint="Stays off the public calendar.">
          <textarea value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} rows={2} placeholder="Contacts, load-in, reminders…" style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
        </Field>

        {error && <div style={{ color: "#ff8a8a", fontSize: "0.82rem", padding: "0.5rem 0.75rem", background: "rgba(201,68,68,0.12)", border: "1px solid rgba(201,68,68,0.4)", borderRadius: "8px" }}>{error}</div>}

        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "0.2rem" }}>
          <button onClick={onClose} disabled={saving} style={ghostBtn}>Cancel</button>
          <button onClick={save} disabled={!canSave || saving} style={{ ...primaryBtn, opacity: !canSave ? 0.5 : 1, cursor: !canSave ? "not-allowed" : "pointer" }}>
            {saving ? "Saving…" : "✓ Save event"}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

// ── Shared bits ─────────────────────────────────────────────────────────────

function Overlay({ onClose, children }) {
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(6,12,32,0.78)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "8vh 1rem 2rem", zIndex: 60, overflowY: "auto" }}
    >
      <div style={{ background: BRAND.panel, border: `1px solid ${BRAND.line}`, borderRadius: "14px", padding: "1rem 1.1rem 1.2rem", width: "100%", maxWidth: "480px", boxShadow: "0 12px 48px rgba(0,0,0,0.5)" }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, hint, children, grow = 1 }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.22rem", flex: `${grow} 1 130px`, minWidth: 0 }}>
      <span style={{ color: BRAND.cream, fontSize: "0.76rem", fontWeight: 600 }}>{label}</span>
      {children}
      {hint && <span style={{ color: BRAND.dim, fontSize: "0.68rem" }}>{hint}</span>}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  background: BRAND.card,
  border: `1px solid ${BRAND.line}`,
  borderRadius: "8px",
  color: "white",
  fontSize: "0.9rem",
  padding: "0.5rem 0.65rem",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const ghostBtn = {
  background: "transparent",
  color: BRAND.muted,
  border: `1px solid ${BRAND.line}`,
  borderRadius: "8px",
  padding: "0.4rem 0.75rem",
  fontSize: "0.8rem",
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};

const navBtn = {
  background: BRAND.card,
  color: "white",
  border: `1px solid ${BRAND.line}`,
  borderRadius: "8px",
  padding: "0.35rem 0.85rem",
  fontSize: "1.05rem",
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
  lineHeight: 1,
};

const primaryBtn = {
  background: BRAND.green,
  color: "white",
  border: "none",
  borderRadius: "8px",
  padding: "0.55rem 1.1rem",
  fontSize: "0.92rem",
  fontWeight: 800,
  fontFamily: "inherit",
  cursor: "pointer",
  width: "100%",
};
